import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE = '/api/v1/user';

export interface Bookmark {
  id: string;
  judgmentId: number;
  notes: string | null;
  tags: string[];
  collectionId: string | null;
  createdAt: string;
  judgment: {
    id: number;
    caseNumbers: string[];
    courtType: string;
    judgmentDate: string | null;
    summary: string | null;
  };
}

interface BookmarksResponse {
  bookmarks: Bookmark[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function useBookmarks() {
  const { isAuthenticated } = useAuth();
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    return {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
    };
  }, []);

  const addBookmark = useCallback(
    async (judgmentId: number, notes?: string) => {
      if (!isAuthenticated) return null;

      try {
        const response = await fetch(`${API_BASE}/bookmarks`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ judgmentId, notes }),
        });

        if (!response.ok) {
          throw new Error('Failed to add bookmark');
        }

        setBookmarkedIds((prev) => new Set([...prev, judgmentId]));
        return await response.json();
      } catch (error) {
        console.error('Error adding bookmark:', error);
        return null;
      }
    },
    [isAuthenticated, getAuthHeaders]
  );

  const removeBookmark = useCallback(
    async (judgmentId: number) => {
      if (!isAuthenticated) return false;

      try {
        const response = await fetch(`${API_BASE}/bookmarks/${judgmentId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error('Failed to remove bookmark');
        }

        setBookmarkedIds((prev) => {
          const next = new Set(prev);
          next.delete(judgmentId);
          return next;
        });
        return true;
      } catch (error) {
        console.error('Error removing bookmark:', error);
        return false;
      }
    },
    [isAuthenticated, getAuthHeaders]
  );

  const toggleBookmark = useCallback(
    async (judgmentId: number) => {
      if (bookmarkedIds.has(judgmentId)) {
        return removeBookmark(judgmentId);
      } else {
        return addBookmark(judgmentId);
      }
    },
    [bookmarkedIds, addBookmark, removeBookmark]
  );

  const isBookmarked = useCallback(
    (judgmentId: number) => bookmarkedIds.has(judgmentId),
    [bookmarkedIds]
  );

  const checkBookmarks = useCallback(
    async (judgmentIds: number[]) => {
      if (!isAuthenticated || judgmentIds.length === 0) return;

      try {
        const response = await fetch(`${API_BASE}/bookmarks/check`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ judgmentIds }),
        });

        if (response.ok) {
          const data = await response.json();
          setBookmarkedIds(new Set(data.bookmarkedIds));
        }
      } catch (error) {
        console.error('Error checking bookmarks:', error);
      }
    },
    [isAuthenticated, getAuthHeaders]
  );

  const getBookmarks = useCallback(
    async (page: number = 1, limit: number = 20, collectionId?: string): Promise<BookmarksResponse | null> => {
      if (!isAuthenticated) return null;

      setIsLoading(true);
      try {
        let url = `${API_BASE}/bookmarks?page=${page}&limit=${limit}`;
        if (collectionId) {
          url += `&collectionId=${encodeURIComponent(collectionId)}`;
        }

        const response = await fetch(url, { headers: getAuthHeaders() });

        if (!response.ok) {
          throw new Error('Failed to get bookmarks');
        }

        return await response.json();
      } catch (error) {
        console.error('Error getting bookmarks:', error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, getAuthHeaders]
  );

  const updateBookmarkNotes = useCallback(
    async (bookmarkId: string, notes: string): Promise<boolean> => {
      if (!isAuthenticated) return false;

      try {
        const response = await fetch(`${API_BASE}/bookmarks/${bookmarkId}`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({ notes }),
        });

        if (!response.ok) {
          throw new Error('Failed to update bookmark notes');
        }

        return true;
      } catch (error) {
        console.error('Error updating bookmark notes:', error);
        return false;
      }
    },
    [isAuthenticated, getAuthHeaders]
  );

  // Clear bookmarked IDs when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setBookmarkedIds(new Set());
    }
  }, [isAuthenticated]);

  return {
    bookmarkedIds,
    isLoading,
    addBookmark,
    removeBookmark,
    toggleBookmark,
    isBookmarked,
    checkBookmarks,
    getBookmarks,
    updateBookmarkNotes,
  };
}
